import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUsers, FiSearch, FiShield, FiUser, FiMail, FiPhone,
  FiCalendar, FiPackage, FiEdit2, FiX, FiCheck, FiAlertCircle
} from 'react-icons/fi';
import { getUsers, updateUserRole, getOrders } from '../../firebase/database';
import { formatDate, formatPrice } from '../../utils/helpers';
import { Spinner } from '../../components/common/Loader';
import toast from 'react-hot-toast';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleModal, setRoleModal] = useState(null); // user being role-changed
  const [updatingRole, setUpdatingRole] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [u, o] = await Promise.all([getUsers(), getOrders()]);
      setUsers(u);
      setOrders(o);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (uid, newRole) => {
    if (!window.confirm(`Change this user's role to "${newRole}"?`)) return;
    setUpdatingRole(true);
    try {
      await updateUserRole(uid, newRole);
      setUsers(prev => prev.map(u => u.id === uid ? { ...u, role: newRole } : u));
      if (selectedUser?.id === uid) setSelectedUser(prev => ({ ...prev, role: newRole }));
      setRoleModal(null);
      toast.success(`Role updated to ${newRole}`);
    } catch {
      toast.error('Failed to update role');
    } finally {
      setUpdatingRole(false);
    }
  };

  const getUserOrders = (uid) => orders.filter(o => o.userId === uid);
  const getUserRevenue = (uid) => getUserOrders(uid).filter(o => o.status !== 'cancelled').reduce((s, o) => s + (o.total || 0), 0);

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.phone?.includes(search);
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const totalAdmins = users.filter(u => u.role === 'admin').length;
  const totalUsers = users.filter(u => u.role !== 'admin').length;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-2xl font-black text-gray-900">Users Management</h1>
        <p className="text-gray-500 text-sm mt-1">{users.length} total registered users</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total Users', value: users.length, icon: FiUsers, color: 'bg-blue-500' },
          { label: 'Customers', value: totalUsers, icon: FiUser, color: 'bg-green-500' },
          { label: 'Admins', value: totalAdmins, icon: FiShield, color: 'bg-[#e63946]' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white shadow-card p-5 flex items-center gap-4">
            <div className={`w-12 h-12 ${color} flex items-center justify-center flex-shrink-0`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-black text-gray-900">{value}</p>
              <p className="text-gray-500 text-xs">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by name, email, phone..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input-field pl-9 w-64"
          />
        </div>
        <div className="flex border border-gray-200">
          {[
            { value: 'all', label: 'All' },
            { value: 'user', label: 'Customers' },
            { value: 'admin', label: 'Admins' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setRoleFilter(opt.value)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${roleFilter === opt.value ? 'bg-[#1a3a6e] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Users Table */}
        <div className="lg:col-span-3 bg-white shadow-card overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['User', 'Contact', 'Role', 'Orders', 'Joined', 'Actions'].map(h => (
                      <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(user => {
                    const userOrders = getUserOrders(user.id);
                    return (
                      <motion.tr
                        key={user.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        onClick={() => setSelectedUser(user)}
                        className={`hover:bg-gray-50 cursor-pointer transition-colors ${selectedUser?.id === user.id ? 'bg-[#1a3a6e]/5 border-l-2 border-[#1a3a6e]' : ''}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${user.role === 'admin' ? 'bg-[#e63946] text-white' : 'bg-[#1a3a6e] text-white'}`}>
                              {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900 text-sm">{user.name || 'Unknown'}</p>
                              <p className="text-gray-400 text-xs truncate max-w-[120px]">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-xs">{user.phone || '—'}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${user.role === 'admin' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                            {user.role === 'admin' ? <FiShield className="w-3 h-3" /> : <FiUser className="w-3 h-3" />}
                            {user.role === 'admin' ? 'Admin' : 'Customer'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-semibold text-gray-900">{userOrders.length}</span>
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-xs">{formatDate(user.createdAt)}</td>
                        <td className="py-3 px-4">
                          <button
                            onClick={(e) => { e.stopPropagation(); setRoleModal(user); }}
                            className="p-1.5 text-gray-500 hover:text-[#1a3a6e] hover:bg-[#1a3a6e]/10 rounded transition-colors"
                            title="Change Role"
                          >
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="text-center py-16 text-gray-400">
                  <FiUsers className="w-10 h-10 mx-auto mb-2" />
                  <p>No users found</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Detail Panel */}
        <div className="lg:col-span-2">
          {selectedUser ? (
            <div className="bg-white shadow-card p-5 sticky top-4 space-y-5">
              {/* Profile */}
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center font-black text-2xl ${selectedUser.role === 'admin' ? 'bg-[#e63946] text-white' : 'bg-[#1a3a6e] text-white'}`}>
                  {selectedUser.name?.[0]?.toUpperCase() || 'U'}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{selectedUser.name || 'Unknown'}</h3>
                  <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${selectedUser.role === 'admin' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                    {selectedUser.role === 'admin' ? <FiShield className="w-3 h-3" /> : <FiUser className="w-3 h-3" />}
                    {selectedUser.role === 'admin' ? 'Admin' : 'Customer'}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="space-y-2.5">
                {[
                  { icon: FiMail, label: 'Email', value: selectedUser.email },
                  { icon: FiPhone, label: 'Phone', value: selectedUser.phone || 'Not provided' },
                  { icon: FiCalendar, label: 'Joined', value: formatDate(selectedUser.createdAt) },
                  { icon: FiPackage, label: 'Total Orders', value: `${getUserOrders(selectedUser.id).length} orders` },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-center gap-3 text-sm">
                    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div>
                      <span className="text-gray-400 text-xs">{label}</span>
                      <p className="text-gray-900 font-medium">{value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Revenue */}
              {getUserOrders(selectedUser.id).length > 0 && (
                <div className="bg-green-50 border border-green-200 p-3">
                  <p className="text-xs text-green-600 font-bold uppercase tracking-wider mb-1">Total Spent</p>
                  <p className="text-xl font-black text-green-700">{formatPrice(getUserRevenue(selectedUser.id))}</p>
                </div>
              )}

              {/* Orders */}
              {getUserOrders(selectedUser.id).length > 0 && (
                <div>
                  <h4 className="font-bold text-sm text-gray-700 mb-2">Recent Orders</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {getUserOrders(selectedUser.id).slice(0, 5).map(o => (
                      <div key={o.id} className="flex items-center justify-between text-xs bg-gray-50 px-3 py-2">
                        <span className="font-mono font-bold text-[#1a3a6e]">#{o.orderId || o.id?.slice(0,8).toUpperCase()}</span>
                        <span className={`px-2 py-0.5 rounded-full font-semibold capitalize ${
                          { pending: 'bg-yellow-100 text-yellow-700', delivered: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700', shipped: 'bg-blue-100 text-blue-700' }[o.status] || 'bg-gray-100 text-gray-600'
                        }`}>{o.status}</span>
                        <span className="font-bold">{formatPrice(o.total || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Role Change */}
              <div className="border-t pt-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Change Role</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRoleChange(selectedUser.id, 'user')}
                    disabled={selectedUser.role === 'user' || updatingRole}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-all ${selectedUser.role === 'user' ? 'bg-blue-100 text-blue-700 cursor-not-allowed' : 'border border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                  >
                    <FiUser className="w-4 h-4" />
                    Customer
                    {selectedUser.role === 'user' && <FiCheck className="w-3 h-3" />}
                  </button>
                  <button
                    onClick={() => handleRoleChange(selectedUser.id, 'admin')}
                    disabled={selectedUser.role === 'admin' || updatingRole}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold transition-all ${selectedUser.role === 'admin' ? 'bg-red-100 text-red-700 cursor-not-allowed' : 'border border-red-300 text-red-600 hover:bg-red-50'}`}
                  >
                    <FiShield className="w-4 h-4" />
                    Admin
                    {selectedUser.role === 'admin' && <FiCheck className="w-3 h-3" />}
                  </button>
                </div>
                {selectedUser.role === 'admin' && (
                  <p className="text-xs text-orange-500 mt-2 flex items-center gap-1">
                    <FiAlertCircle className="w-3 h-3" />
                    Admins have full access to this panel
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white shadow-card p-10 text-center text-gray-400">
              <FiUsers className="w-12 h-12 mx-auto mb-3" />
              <p className="text-sm">Click on a user to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
